import { zodResolver } from '@hookform/resolvers/zod';
import cloneDeep from 'lodash/cloneDeep';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { SelectOption } from '@/shared/types';
import { ConditionView, FormSelect } from '@/shared/ui';

import { getCountrySelect } from '@/api/apiService';

import { IdConstants } from '../../../components/Forms/constants';

import { optionalCountrySchema } from './model/schemas';
import {
  OptionalAddressBlockSchema,
  InternationalAddressSchema,
  UkranianAddressSchema,
  CountrySchema,
} from './model/types';
import { InternationalAddressBlock } from './ui/InternationalAddressBlock';
import { UkranianAddressBlock } from './ui/UkranianAddressBlock';

interface BaseProps {
  onlyUkraine?: boolean;
  includeZipCode?: boolean;
  revalidate: boolean;
  hideCountry?: boolean;
  hideAppartments?: boolean;
  address: OptionalAddressBlockSchema;
  withCustomStreet?: boolean;
  handleAddressChange: (newAddress: Partial<OptionalAddressBlockSchema>) => void;
}

export const OptionnalAddressBlock: React.FC<BaseProps> = ({
  address,
  onlyUkraine = false,
  includeZipCode = false,
  hideCountry = false,
  hideAppartments = false,
  withCustomStreet = false,
  revalidate,
  handleAddressChange,
}) => {
  const { country: addressCountry, ...addressWithoutCountry } = address;
  const { control, watch, getValues, trigger } = useForm<CountrySchema>({
    resolver: zodResolver(optionalCountrySchema),
    defaultValues: { country: addressCountry },
  });
  const country = watch('country');

  const [optionsCountry, setOptionsCountry] = useState<SelectOption[]>([]);

  const isUkraineSelected = useMemo<boolean>(() => country?.id === IdConstants.UKRAINE, [country?.id]);

  useEffect(() => {
    revalidate && trigger();
  }, [revalidate]);

  useEffect(() => {
    getCountrySelect(setOptionsCountry);
  }, []);

  const handleChange = async (newAddressWithoutCountry: Partial<typeof addressWithoutCountry>): Promise<void> => {
    revalidate && trigger();

    const newAddress = cloneDeep({
      ...getValues(),
      ...newAddressWithoutCountry,
    });

    handleAddressChange(newAddress);
  };

  return (
    <>
      {!hideCountry && (
        <FormSelect
          name="country"
          control={control}
          label="Країна"
          options={optionsCountry}
          disabled={onlyUkraine}
          onChange={() => handleChange({})}
        />
      )}

      <ConditionView
        if={isUkraineSelected}
        else={
          <InternationalAddressBlock
            address={addressWithoutCountry as InternationalAddressSchema}
            revalidate={revalidate}
            handleAddressChange={handleChange}
            hideAppartments={hideAppartments}
          />
        }
      >
        <UkranianAddressBlock
          withCustomStreet={withCustomStreet}
          address={addressWithoutCountry as UkranianAddressSchema}
          revalidate={revalidate}
          country={country}
          includeZipCode={includeZipCode}
          handleAddressChange={handleChange}
          hideAppartments={hideAppartments}
        />
      </ConditionView>
    </>
  );
};

export const optionalAddressBlockPreview = ({
  address,
  includeZipCode,
  hideAppartments = false,
  enableCustomAddressFields = false,
}: {
  address: Partial<OptionalAddressBlockSchema>;
  includeZipCode?: boolean;
  hideAppartments?: boolean;
  enableCustomAddressFields?: boolean;
}): Array<[string, Nullable<string>]> => {
  const settlementIsCustom = enableCustomAddressFields && address?.settlement?.id === 'custom';
  const streetIsCustom = enableCustomAddressFields && address?.street?.id === 'custom';

  const streetValue =
    settlementIsCustom || streetIsCustom ? (address?.custom_street ?? null) : address?.street?.label || address?.street;

  const labelValueListData: Array<[string, Nullable<string>]> = [
    ['Країна', address?.country?.label],
    ['Область', address?.region?.label],
    ['Район', address?.district?.label],
    ['Населений пункт', (address?.settlement as SelectOption)?.label || (address?.settlement as unknown as string)],
    ['Вулиця', streetValue],
    ['Номер будинку', address?.house],
    ['Номер корпусу', address?.building],
    ['Номер квартири', address?.apartment],
  ];

  if (hideAppartments) {
    labelValueListData.pop();
    labelValueListData.pop();
    labelValueListData.pop();
  }

  if (includeZipCode) labelValueListData.push(['Індекс', address?.zip_code]);

  return labelValueListData;
};
